import React, { Suspense, useState, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import DrawerV6 from '../Common/Drawer/DrawerV6/DrawerV6';
import Icon from '../Common/Icon';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../Common/Notification';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useLoadOrderTasksFilters, useOrderBaselineTasks } from '../../apis/orderTasks';
import { OrderTask, LoadOrderTasksFiltersRequest } from '../../apis/orderTasks/orderTasks.types';

// DropResult interface for drag and drop
interface DropResult {
	destination?: {
		index: number;
		droppableId: string;
	} | null;
	source: {
		index: number;
		droppableId: string;
	};
}

// Error Fallback Component
const ErrorFallback: React.FC<{ error: unknown; resetErrorBoundary: () => void }> = ({
	error,
	resetErrorBoundary
}) => (
	<div className="alert alert-danger m-3">
		<h5>Something went wrong loading order tasks:</h5>
		<p>{error instanceof Error ? error.message : String(error)}</p>
		<button className="btn btn-secondary" onClick={resetErrorBoundary}>
			Try again
		</button>
	</div>
);

// Loading Component for Suspense
const LoadingComponent: React.FC = () => (
	<LoadingIndicator isLoading={true}>
		<div>Loading order tasks...</div>
	</LoadingIndicator>
);

// Component Props
interface ComponentProps {
	subscriptionId: string;
	baselineId: string;
	targetDaysOut: string;
	targetStaticDate: string;
	onComplete: () => void;
	onCancel: () => void;
}

// Main Content Component
const OrderTasksDrawerContent: React.FC<ComponentProps> = ({
	subscriptionId,
	baselineId,
	targetDaysOut,
	targetStaticDate,
	onComplete,
	onCancel
}) => {
	// Local state for tasks
	const [tasks, setTasks] = useState<OrderTask[]>([]);

	// Prepare request for loading filters
	const loadFiltersRequest: LoadOrderTasksFiltersRequest = {
		subscriptionId: subscriptionId,
		baselineId,
		targetDaysOut,
		targetStaticDate,
		isActive: true,
	};

	// TanStack Query hooks
	const { data: filtersData, isFetching: isFetchingFilters, error: filtersError } = useLoadOrderTasksFilters(loadFiltersRequest);
	const orderTasksMutation = useOrderBaselineTasks((result) => {
		if (!result.isError) {
			Notification('success', 'Success', 'Baseline Task order saved successfully');
			onComplete()
		} else {
			result.errors.forEach((error: any) => (
				Notification('error', 'An Error Occurred', typeof error === 'string' ? error : error.message || 'Failed to order tasks')
			));
		}
	});

	// Initialize tasks when data loads
	React.useEffect(() => {
		if (filtersError) {
			Notification('error', 'Error Loading Tasks', filtersError.message);
			return;
		}
		if (!filtersData) return;
		if (filtersData.isError || filtersData.errors?.length > 0) {
			const messages = filtersData.errors?.length > 0
				? filtersData.errors.map(e => e.message).join(' ')
				: 'An unknown error occurred';
			Notification('error', 'Error Loading Tasks', messages);
			return;
		}
		if (filtersData.baselineTasks) {
			setTasks(filtersData.baselineTasks);
		}
	}, [filtersData, filtersError]);

	// Handle drag end for reordering
	const onDragEnd = useCallback((result: DropResult): void => {
		// dropped outside the step list area
		if (!result.destination) {
			return;
		}

		const newTasks = tasks.slice();
		const [removed] = newTasks.splice(result.source.index, 1);
		newTasks.splice(result.destination.index, 0, removed);

		/**
		 * Always update the entire list, so the sameDayOrdinal is always 0 - n
		 * We don't know if they're all zero because v4 has known bugs with negatives and other weirdness.
		 * Hopefully this ensures any reordering is done properly with 0 - n values all the time.
		 */
		for (let i = 0; i < newTasks.length; i++) {
			newTasks[i].sameDayOrdinal = i;
		}
		setTasks(newTasks);
	}, [tasks]);

	// Handle save
	const onSave = useCallback(async (): Promise<void> => {
		const request = {
			subscriptionId: subscriptionId,
			baselineId,
			targetDaysOut,
			targetStaticDate,
			baselineTasks: tasks,
		};

		orderTasksMutation.mutate(request);
	}, [subscriptionId, baselineId, targetDaysOut, targetStaticDate, tasks, orderTasksMutation]);

	// UI styling constants
	const ui = {
		brand: '#6860E4',
		panel: 'white',
		panelDrag: '#F7F5FF',
		border: '#E9E7FF',
		borderActive: '#CFCBFF',
	};

	// tried moving this to scss but it just gets overridden from within the component
	const getItemStyle = (isDragging: boolean, draggableStyle?: any) => ({
		userSelect: 'none' as const,
		lineHeight: 1.7,
		margin: '0 0 8px 0',
		background: ui.panel,
		border: `1px solid ${isDragging ? ui.borderActive : ui.border}`,
		borderRadius: 14,
		padding: 12,
		boxShadow: isDragging ? `0 2px 8px rgba(0,0,0,0.1), 0 0 0 3px ${ui.brand}20` : '0 1px 3px rgba(0,0,0,0.1)',
		transform: isDragging ? 'scale(1.005)' : 'none',
		transition: 'background .15s ease, border-color .15s ease, box-shadow .15s ease, transform .06s ease',

		...draggableStyle,
	});

	const getListStyle = (isDraggingOver: boolean) => ({
		background: isDraggingOver ? ui.panelDrag : ui.panel,
		borderRadius: '0.25rem',
		padding: 8,
		transition: 'background .2s ease, border-color .2s ease',
		height: '100%',
		maxHeight: '80vh',
		overflowY: 'auto' as const,
		minHeight: '200px',
	});

	return (
		<div className='order-tasks-drawer'>
			<DrawerV6
				isWide={true}
				title='Order Tasks'
				onCancelClick={onCancel}
				onSaveClick={onSave}
				isSaveDisabled={orderTasksMutation.isPending}
			>
				<LoadingIndicator isLoading={orderTasksMutation.isPending || isFetchingFilters}>
					<div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
						<DragDropContext onDragEnd={onDragEnd}>
							{/* @ts-expect-error - React version compatibility issue with @hello-pangea/dnd */}
							<Droppable droppableId='droppable'>
								{(provided: any, snapshot: any) => (
									<div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
										{tasks.map((task: OrderTask, index: number) => (
											<Draggable key={task.id} draggableId={task.id.toString()} index={index}>
												{(provided: any, snapshot: any) => (
													<div
														className='task row'
														ref={provided.innerRef}
														{...provided.draggableProps}
														{...provided.dragHandleProps}
														style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}>
														<div className='col-1'>
															<div className='bars' style={{ color: '#6860E4' }}>
																<Icon name='bars' />
															</div>
														</div>
														<div className='col-11 task-name pt-2' style={{ color: '#6860E4' }}>
															{task.name}
														</div>
													</div>
												)}
											</Draggable>
										))}
										{provided.placeholder}
									</div>
								)}
							</Droppable>
						</DragDropContext>
					</div>
				</LoadingIndicator>
			</DrawerV6>
		</div>
	);
};

// Main Export Component with Error Boundary and Suspense
const OrderTasksDrawer: React.FC<ComponentProps> = (props) => {
	return (
		<ErrorBoundary
			FallbackComponent={ErrorFallback}
			onReset={() => window.location.reload()}
		>
			<Suspense fallback={<LoadingComponent />}>
				<OrderTasksDrawerContent {...props} />
			</Suspense>
		</ErrorBoundary>
	);
};

export default OrderTasksDrawer;